import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { reissueQrPayment } from "../../services";
import { formatDateTimeBkk } from "../../utils/date";
import { QrPayment, QrPaymentListItem } from "../../types/qr-payment.type";

/**
 * The server answers in English — its messages are read by developers and other services as
 * much as by this screen. Mapped on the exact string, and an unmapped one falls through to
 * itself: inventing a friendly Thai sentence for a refusal nobody anticipated is how an
 * operator ends up confidently doing the wrong thing.
 */
const REFUSAL_TH: Record<string, string> = {
  'Only a QR closed as "the customer will transfer later" can be replaced this way.':
    "ออกใหม่ได้เฉพาะบิลที่ปิดแบบ “ลูกค้าจะโอนภายหลัง” เท่านั้น",
  "This QR can still be paid, so the customer does not need a new one.":
    "QR ใบเดิมยังจ่ายได้อยู่ ลูกค้าไม่ต้องใช้ใบใหม่",
  "This bill has already been paid, so it needs no new QR code.":
    "บิลนี้ได้รับชำระเรียบร้อยแล้ว ไม่ต้องออก QR ใหม่",
  "No active bill matches this bill_uuid.": "ไม่พบบิลของรายการนี้แล้ว กรุณาตรวจสอบกับสาขา",
  "No active customer matches this customer_uuid.":
    "ไม่พบข้อมูลลูกค้าของรายการนี้แล้ว กรุณาตรวจสอบกับสาขา",
  "Failed to raise a replacement QR code.": "ออก QR ใหม่ไม่สำเร็จ กรุณาลองใหม่",
};

const refusalText = (error: unknown): string => {
  const message = (error as AxiosError<{ message?: string }>)?.response?.data?.message;
  if (!message) return "ออก QR ใหม่ไม่สำเร็จ กรุณาลองใหม่";
  return REFUSAL_TH[message] ?? message;
};

const money = (value: string): string =>
  Number(value).toLocaleString("th-TH", { minimumFractionDigits: 2 });

/**
 * Hands a customer who promised a transfer a code that actually works.
 *
 * Two steps on purpose. The confirm step is not ceremony: this asks the bank for a new code
 * against a sale someone already closed, and the operator should see which sale and for how
 * much before it happens.
 *
 * The second step is the point of the whole feature — the new QR itself, big enough to
 * screenshot and send. Reissuing without showing the code would raise something at the bank
 * and leave the customer exactly as unable to pay as before.
 */
export default function ReissueDialog({
  row,
  onClose,
  onReissued,
}: Readonly<{
  row: QrPaymentListItem | null;
  onClose: () => void;
  onReissued: () => void;
}>) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<QrPayment | null>(null);

  // Reopening on another row must not show the previous row's code — the two look alike, and
  // sending the wrong customer a QR for someone else's bill is not a recoverable mistake.
  useEffect(() => {
    setError(null);
    setCreated(null);
    setSubmitting(false);
  }, [row?.uuid]);

  const handleReissue = async () => {
    if (!row) return;
    setSubmitting(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await reissueQrPayment(row.uuid);
      setCreated(data as QrPayment);
      // Told now rather than on close: the list behind this dialog has gained a row and the
      // old one has not moved, and an operator who closes with the browser chrome should not
      // be looking at a stale table either.
      onReissued();
    } catch (caught) {
      setError(refusalText(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(row)} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {created ? "QR ใหม่พร้อมส่งให้ลูกค้า" : "ออก QR ใหม่ให้ลูกค้า"}
      </DialogTitle>

      <DialogContent dividers>
        {!created && row && (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              บิลนี้ถูกปิดไว้แบบ “ลูกค้าจะโอนภายหลัง” และ QR ใบเดิมหมดอายุแล้ว
              ระบบจะขอรหัสใหม่จากธนาคารสำหรับบิลและยอดเดิม
            </Typography>

            <Divider />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                ยอด
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {money(row.amount)} บาท
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                อ้างอิงเดิม
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {row.ref1}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                หมดอายุเมื่อ
              </Typography>
              <Typography variant="body2">{formatDateTimeBkk(row.expires_at) || "—"}</Typography>
            </Stack>

            {/* The settled row is the record of WHY this sale was let go unpaid, and it stays
                exactly as it is. Said out loud because "ออกใหม่" could reasonably be read as
                replacing that record rather than adding to it. */}
            <Alert severity="info" sx={{ mt: 1 }}>
              รายการเดิมจะยังอยู่ในระบบตามเดิม ไม่ถูกลบหรือแก้ไข
            </Alert>
          </Stack>
        )}

        {created && (
          <Stack spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary" textAlign="center">
              ส่งรูปนี้ให้ลูกค้าสแกนจ่ายได้เลย
            </Typography>

            {created.qr_image ? (
              // The bank's own image — it carries the PromptPay mark SCB requires on a
              // displayed QR30. Drawing the payload ourselves would not.
              <Box
                component="img"
                src={`data:image/png;base64,${created.qr_image}`}
                alt="QR Code สำหรับชำระเงิน"
                sx={{ width: 240, height: 240, display: "block", bgcolor: "#fff", p: 1, borderRadius: 1 }}
              />
            ) : (
              <Alert severity="warning">
                ธนาคารไม่ได้ส่งรูป QR กลับมา กรุณาลองออกใหม่อีกครั้ง
              </Alert>
            )}

            <Typography variant="h6" fontWeight={700}>
              {money(created.amount)} บาท
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={`อ้างอิง ${created.ref1}`}
              sx={{ fontFamily: "monospace" }}
            />
            {/* The one thing that makes this code different from the last, and the reason to
                send it today rather than tomorrow. */}
            <Typography variant="body2" color="warning.main" textAlign="center">
              หมดอายุ {formatDateTimeBkk(created.expires_at) || "—"}
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {created ? "ปิด" : "ยกเลิก"}
        </Button>
        {!created && (
          <Button
            variant="contained"
            onClick={handleReissue}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? "กำลังขอรหัสใหม่..." : "ออก QR ใหม่"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
